import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { isSignupCallback, signupLinkExpired, signupLinkFailed } from "../lib/signupLinkParams";
import { clearedOnboardingMetadata, readPendingOnboarding } from "../lib/signupOnboarding";
import { reportError } from "../lib/errorReporter";

// How long the screen waits for Supabase to establish the confirmed session
// before it declares the link unusable.
const SESSION_TIMEOUT_MS = 10000;
const REDIRECT_DELAY_MS = 2500;

export default function ConfirmSignup() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { loading, session, user, orgs, orgsLoaded, bootstrap, signOut } = useAuth();
  const [phase, setPhase] = useState(() => (signupLinkFailed() ? "link_error" : "verifying"));
  const [pending, setPending] = useState(null);
  // Synchronous guards: the bootstrap runs exactly once per visit even under a
  // double effect invocation, a refresh mid-flight or a duplicated callback.
  const bootstrapping = useRef(false);
  const settled = useRef(false);

  const finish = useCallback(async () => {
    settled.current = true;
    // The user must sign in again with the credentials created at registration.
    await signOut();
    setPhase("success");
  }, [signOut]);

  const complete = useCallback(
    async (data) => {
      if (bootstrapping.current) return;
      bootstrapping.current = true;
      setPhase("completing");
      try {
        // The existing idempotent organization bootstrap is authoritative: it
        // validates the payload, assigns the owner role server-side and returns
        // the existing organization when one is already there.
        await bootstrap({ fullName: data.fullName, organizationName: data.organizationName });
      } catch (err) {
        reportError("auth.signup_bootstrap", err);
        bootstrapping.current = false;
        setPhase("failed");
        return;
      }
      try {
        await supabase.auth.updateUser({ data: clearedOnboardingMetadata() });
      } catch (err) {
        reportError("auth.signup_onboarding_clear");
      }
      await finish();
    },
    [bootstrap, finish],
  );

  useEffect(() => {
    if (settled.current || phase !== "verifying" || loading) return undefined;
    // A genuine Supabase session is required. URL parameters alone never start
    // onboarding.
    if (!session) {
      if (!isSignupCallback()) {
        settled.current = true;
        setPhase("link_error");
        return undefined;
      }
      const timer = setTimeout(() => {
        if (settled.current) return;
        settled.current = true;
        setPhase("link_error");
      }, SESSION_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
    if (!orgsLoaded) return undefined;
    // Already onboarded: never create a second organization.
    if (orgs.length > 0) {
      if (isSignupCallback()) {
        void finish();
        return undefined;
      }
      settled.current = true;
      setPhase("already");
      return undefined;
    }
    const data = readPendingOnboarding(user);
    if (!data) {
      settled.current = true;
      setPhase("no_pending");
      return undefined;
    }
    setPending(data);
    void complete(data);
    return undefined;
  }, [phase, loading, session, orgsLoaded, orgs, user, complete, finish]);

  useEffect(() => {
    if (phase !== "success") return undefined;
    const timer = setTimeout(
      () => nav("/login", { replace: true, state: { signupConfirmed: true } }),
      REDIRECT_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [phase, nav]);

  const restart = useCallback(async () => {
    await signOut();
    nav("/register", { replace: true });
  }, [nav, signOut]);

  const body = () => {
    if (phase === "verifying") {
      return (
        <div className="qw-empty" data-testid="confirm-signup-verifying">
          {t("signup.verifying")}
        </div>
      );
    }

    if (phase === "completing") {
      return (
        <div className="qw-empty" data-testid="confirm-signup-completing">
          {t("signup.completing")}
        </div>
      );
    }

    if (phase === "link_error") {
      return (
        <div data-testid="confirm-signup-link-error">
          <h1>{t("signup.link_invalid_title")}</h1>
          <p className="lead">
            {signupLinkExpired() ? t("signup.link_expired_body") : t("signup.link_invalid_body")}
          </p>
          <Link className="qw-btn primary" to="/register" data-testid="confirm-signup-request-new-link">
            {t("signup.restart")}
          </Link>
          <div style={{ marginTop: 16, fontSize: 13 }}>
            <Link to="/login" data-testid="confirm-signup-back-to-login" style={{ color: "#0f766e", fontWeight: 600 }}>
              {t("signup.back_to_login")}
            </Link>
          </div>
        </div>
      );
    }

    if (phase === "failed") {
      return (
        <div data-testid="confirm-signup-failed">
          <h1>{t("signup.failed_title")}</h1>
          <p className="lead">{t("signup.failed_body")}</p>
          <button
            type="button"
            className="qw-btn primary"
            onClick={() => complete(pending)}
            disabled={!pending}
            data-testid="confirm-signup-retry"
          >
            {t("signup.retry")}
          </button>
        </div>
      );
    }

    if (phase === "no_pending") {
      return (
        <div data-testid="confirm-signup-no-pending">
          <h1>{t("signup.failed_title")}</h1>
          <p className="lead">{t("signup.no_pending_body")}</p>
          <button type="button" className="qw-btn primary" onClick={restart} data-testid="confirm-signup-restart">
            {t("signup.restart")}
          </button>
        </div>
      );
    }

    if (phase === "already") {
      return (
        <div data-testid="confirm-signup-already">
          <h1>{t("signup.already_title")}</h1>
          <p className="lead">{t("signup.already_body")}</p>
          <Link className="qw-btn primary" to="/dashboard" data-testid="confirm-signup-go-to-app">
            {t("signup.go_to_app")}
          </Link>
        </div>
      );
    }

    return (
      <div data-testid="confirm-signup-success">
        <h1>{t("signup.success_title")}</h1>
        <p className="lead">{t("signup.confirmed_notice")}</p>
        <Link className="qw-btn primary" to="/login" data-testid="confirm-signup-go-to-login">
          {t("signup.go_to_login")}
        </Link>
      </div>
    );
  };

  return (
    <div className="qw-auth-wrap">
      <div className="qw-auth-side">
        <div>
          <div className="brand">OFFERTRACE</div>
          <div className="head">{t("signup.confirm_title")}</div>
          <div className="sub">{t("signup.confirm_side_note")}</div>
        </div>
        <div style={{ opacity: 0.7, fontSize: 12 }}>© OfferTrace</div>
      </div>
      <div className="qw-auth-panel">
        <div className="qw-auth-form" data-testid="confirm-signup-panel">
          {body()}
        </div>
      </div>
    </div>
  );
}
